import { __extends } from "tslib";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import { InstancedLinesMesh, LinesMesh } from "@babylonjs/core/Meshes/linesMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { isPointInFrustum, isTriangleInFrustum, Rectangle, segmentRectangleIntersecting, triangleRectangleIntersecting } from "../utils/MathUtils";
import { CoordinatesType, View } from "../view/View";
import { AbstractTool } from "./AbstractTool";
import { InteractionMode } from "./Tool";
var RectangleSelect = /** @class */ (function (_super) {
    __extends(RectangleSelect, _super);
    function RectangleSelect(pApp) {
        var _this = _super.call(this, pApp) || this;
        _this.multiSelectMode = false;
        _this.mode = "Complete";
        _this.mPartialMode = false;
        _this.mRectDiv = null;
        _this.mView = _this.mApp.viewer.view;
        return _this;
    }
    RectangleSelect.prototype.start = function (pParameter) {
        return (_super.prototype.start.call(this, pParameter));
    };
    RectangleSelect.prototype.stop = function () {
        if (this.mRectDiv != null) {
            this.mView.overlayDiv.removeChild(this.mRectDiv);
            this.mRectDiv = null;
        }
        _super.prototype.stop.call(this);
    };
    RectangleSelect.prototype.handlePointerDown = function (pEvent, pState) {
        if (!pState.isPrimaryPointer(pEvent))
            return;
        this.startSelection();
    };
    RectangleSelect.prototype.handlePointerMove = function (pEvent, pState) {
        if (!pState.isPrimaryPointer(pEvent) || this.mRectDiv == null)
            return;
        var tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return;
        var tP1 = tPointer.initialPosition;
        var tP2 = tPointer.position;
        var tX = Math.min(tP1.x, tP2.x);
        var tY = Math.min(tP1.y, tP2.y);
        var tWidth = Math.abs(tP1.x - tP2.x);
        var tHeight = Math.abs(tP1.y - tP2.y);
        var tPos = new Vector2(tX, tY);
        this.mView.convertCoordsToRef(tPos, CoordinatesType.Client, CoordinatesType.Viewer, tPos);
        this.mRectDiv.style.left = tPos.x.toString() + "px";
        this.mRectDiv.style.top = tPos.y.toString() + "px";
        this.mRectDiv.style.width = tWidth.toString() + "px";
        this.mRectDiv.style.height = tHeight.toString() + "px";
        if (this.mode === "Both") {
            this.setPartialMode(tP2.x < tP1.x, false);
        }
    };
    RectangleSelect.prototype.handlePointerUp = function (pEvent, pState) {
        if (!pState.isPrimaryPointer(pEvent))
            return;
        var tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer != null && this.mRectDiv != null) {
            var tCenter = Vector2.Center(tPointer.initialPosition, tPointer.position);
            var tSizeX = Math.abs(tPointer.initialPosition.x - tPointer.position.x);
            var tSizeY = Math.abs(tPointer.initialPosition.y - tPointer.position.y);
            if (tSizeX > 1 && tSizeY > 1) {
                var tFrustum = this.mView.createPickingFrustum(tCenter, tSizeX, tSizeY, CoordinatesType.Client);
                var tRect = new Rectangle(tPointer.initialPosition, tPointer.position);
                this.select(tFrustum, tRect, this.multiSelectMode || pEvent.ctrlKey);
            }
        }
        this.stop();
    };
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    RectangleSelect.prototype.startSelection = function () {
        this.mRectDiv = document.createElement("div");
        this.mRectDiv.style.position = "absolute";
        this.mRectDiv.style.boxSizing = "border-box";
        this.mView.overlayDiv.appendChild(this.mRectDiv);
        this.setPartialMode(this.mode === "Partial", true);
        this.setInteractionMode(InteractionMode.Primary);
    };
    RectangleSelect.prototype.setPartialMode = function (pMode, pForce) {
        if (pMode === this.mPartialMode && !pForce)
            return;
        this.mPartialMode = pMode;
        this.applyRectDivColor(this.mPartialMode);
    };
    RectangleSelect.prototype.applyRectDivColor = function (pPartialMode) {
        if (this.mRectDiv != null) {
            var tAlpha = pPartialMode ? 0.2 : 0.3;
            var tBorderMode = pPartialMode ? "dashed" : "solid";
            var tCol = Color3.FromHexString(this.mApp.theme.mainColorBright).scale(256);
            var tBGCol = "rgba(" + Math.round(tCol.r) + ", " + Math.round(tCol.g) + ", " + Math.round(tCol.b) + ", " + tAlpha + ")";
            this.mRectDiv.style.backgroundColor = tBGCol;
            this.mRectDiv.style.border = "1px " + tBorderMode + " " + this.mApp.theme.mainColorBright;
        }
    };
    RectangleSelect.prototype.select = function (pFrustum, pRect, pMultiSelect) {
        var tNewSelection = new Array();
        for (var _i = 0, _a = this.mApp.model.elements; _i < _a.length; _i++) {
            var tElement = _a[_i];
            if (this.maySelectElement(pFrustum, pRect, tElement)) {
                tNewSelection.push(tElement);
            }
            else {
                if (tElement.isSelected && pMultiSelect)
                    tNewSelection.push(tElement);
            }
        }
        this.mApp.model.setSelection(tNewSelection);
    };
    RectangleSelect.prototype.maySelectElement = function (pFrustum, pRect, pElement) {
        if (pFrustum == null || !pElement.canSelect)
            return (false);
        //check bounds first
        if (!pElement.boundingBoxVisible.isInFrustum(pFrustum, pElement.transform))
            return (false);
        //check all meshes
        var tNodes = pElement.getNodes(true);
        if (tNodes == null || tNodes.length === 0)
            return (false);
        if (this.mPartialMode) {
            for (var _i = 0, tNodes_1 = tNodes; _i < tNodes_1.length; _i++) {
                var tNode = tNodes_1[_i];
                if (this.isNodePartiallyInFrustum(pFrustum, pRect, tNode)) {
                    return (true);
                }
            }
            return (false);
        }
        else {
            var tIgnoredNodes = 0;
            for (var _a = 0, tNodes_2 = tNodes; _a < tNodes_2.length; _a++) {
                var tNode = tNodes_2[_a];
                var tResult = this.isNodeCompletelyInFrustum(pFrustum, tNode);
                if (tResult === 0) //found a node which is not in frustum -> abort
                    return (false);
                if (tResult < 0)
                    tIgnoredNodes++;
            }
            return (tIgnoredNodes < tNodes.length);
        }
    };
    RectangleSelect.prototype.isNodeCompletelyInFrustum = function (pFrustum, pNode) {
        if (pNode == null || !pNode.isEnabled())
            return (-1); //ignored
        var tVertex = Vector3.Zero();
        if ((pNode instanceof Mesh && pNode.geometry != null) || pNode instanceof InstancedMesh) {
            if (!pNode.isVisible || !pNode.isPickable)
                return (-1); //ignored
            //check bounds first
            if (pNode.isInFrustum(pFrustum)) {
                //check all vertices
                var tPositions = pNode.getVerticesData(VertexBuffer.PositionKind);
                if (tPositions == null)
                    return (-1); //ignored
                //get local frustum
                var tLocalFrustum = View._GetLocalFrustum(pFrustum, pNode);
                //check if all vertices are inside the frustum
                for (var i = 0; i < tPositions.length; i += 3) {
                    tVertex.set(tPositions[i], tPositions[i + 1], tPositions[i + 2]);
                    if (!isPointInFrustum(tVertex, tLocalFrustum)) {
                        return (0); //not in frustum
                    }
                }
            }
            else {
                return (0); //not in frustum
            }
            return (1); //in frustum
        }
        return (-1); //ignored
    };
    RectangleSelect.prototype.isNodePartiallyInFrustum = function (pFrustum, pRect, pNode) {
        if (pNode == null || !pNode.isEnabled())
            return (false);
        if ((pNode instanceof Mesh && pNode.geometry != null) || pNode instanceof InstancedMesh) {
            if (!pNode.isVisible || !pNode.isPickable)
                return (false);
            //check bounds first
            if (pNode.isInFrustum(pFrustum)) {
                //get vertex positions
                if (!pNode._generatePointsArray())
                    return (false);
                var tPositions = pNode._positions;
                if (tPositions == null)
                    return (false);
                //get indices
                var tIndices = pNode.getIndices();
                if (tIndices == null)
                    return (false);
                //get local frustum
                var tLocalFrustum = View._GetLocalFrustum(pFrustum, pNode);
                if (pNode instanceof LinesMesh || pNode instanceof InstancedLinesMesh) {
                    //check if a line segment is partially in the frustum
                    for (var i = 0; i < tIndices.length; i += 2) {
                        var tP1 = tPositions[tIndices[i]];
                        var tP2 = tPositions[tIndices[i + 1]];
                        //check if segment intersects the selection rectangle
                        if (this.segmentRectIntersects(pRect, pNode, tP1, tP2))
                            return (true);
                    }
                }
                else {
                    //check if a triangle is partially in the frustum
                    for (var i = 0; i < tIndices.length; i += 3) {
                        var tP1 = tPositions[tIndices[i]];
                        var tP2 = tPositions[tIndices[i + 1]];
                        var tP3 = tPositions[tIndices[i + 2]];
                        //check if triangle is in frustum (fast check only)
                        if (!isTriangleInFrustum(tP1, tP2, tP3, tLocalFrustum))
                            continue;
                        //check if triangle intersects the selection rectangle
                        if (this.triangleRectIntersects(pRect, pNode, tP1, tP2, tP3))
                            return (true);
                    }
                }
            }
            else
                return (false);
        }
        return (false);
    };
    RectangleSelect.prototype.triangleRectIntersects = function (pRect, pNode, pP1, pP2, pP3) {
        //project triangle to client coordinates
        var tMW = pNode.getWorldMatrix();
        var tP1 = this.mView.projectPoint2(pP1, CoordinatesType.Client, tMW, true);
        var tP2 = this.mView.projectPoint2(pP2, CoordinatesType.Client, tMW, true);
        var tP3 = this.mView.projectPoint2(pP3, CoordinatesType.Client, tMW, true);
        if (tP1 == null || tP2 == null || tP3 == null)
            return (false);
        return (triangleRectangleIntersecting(tP1, tP2, tP3, pRect));
    };
    RectangleSelect.prototype.segmentRectIntersects = function (pRect, pNode, pP1, pP2) {
        //project triangle to client coordinates
        var tMW = pNode.getWorldMatrix();
        var tP1 = this.mView.projectPoint2(pP1, CoordinatesType.Client, tMW, true);
        var tP2 = this.mView.projectPoint2(pP2, CoordinatesType.Client, tMW, true);
        if (tP1 == null || tP2 == null)
            return (false);
        return (segmentRectangleIntersecting(tP1, tP2, pRect));
    };
    return RectangleSelect;
}(AbstractTool));
export { RectangleSelect };
